import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const ParentalControls: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('blog.backToBlog')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          Viewer Guide • August 11, 2026
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          Parental Controls and Safety Features
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          IndeeClips offers content for all ages, but not all content is appropriate for children. As a parent or guardian, you have tools to control what young viewers can access, monitor their activity, and create a safer viewing environment. This guide explains every safety feature available to protect kids on IndeeClips.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Understanding Age Ratings
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Creators set age restrictions when uploading videos. IndeeClips enforces these restrictions:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>All Ages:</strong> Content suitable for everyone, including young children</li>
          <li><strong>13+:</strong> May contain mild language or thematic elements</li>
          <li><strong>18+:</strong> Mature content requiring adult discretion—blocked for users under 18</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Users must enter their birthdate during signup. IndeeClips automatically blocks 18+ content for accounts registered as minors.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Setting Up a Child Account
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Create a Supervised Account
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Log into your parent/guardian account</li>
          <li>Go to Settings → Family & Safety</li>
          <li>Select "Add Supervised Account"</li>
          <li>Enter child's name and accurate birthdate</li>
          <li>Set viewing restrictions based on their age</li>
          <li>Create a unique login for them</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Benefits of Supervised Accounts
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Age-appropriate content filtering automatically enabled</li>
          <li>Parent can review watch history remotely</li>
          <li>Restricted search results (blocks mature terms)</li>
          <li>Commenting disabled or monitored</li>
          <li>No access to private messaging or community features</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Content Filtering Options
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Restricted Mode
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Activate Restricted Mode to hide potentially mature content:
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Go to Settings → Safety & Privacy</li>
          <li>Toggle "Restricted Mode" ON</li>
          <li>Content flagged as mature by creators or community won't appear in search or recommendations</li>
          <li>Lock with password so kids can't disable it</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Note: Restricted Mode uses automated systems and isn't perfect—some inappropriate content may slip through. Combine with active monitoring.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Channel Blocking
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Block specific channels from appearing in your child's feed:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Visit the channel you want to block</li>
          <li>Click three-dot menu → "Block Channel"</li>
          <li>Blocked channels won't show in search, recommendations, or suggested videos</li>
          <li>Manage blocked channels list in Settings</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Monitoring Watch Activity
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Review Watch History
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Regularly check what your child is watching:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Log into their supervised account</li>
          <li>Go to More → Watch History</li>
          <li>Review videos they've watched recently</li>
          <li>If you find inappropriate content, report it and block the channel</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Subscription Monitoring
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Check which channels your child follows:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>View their Subscriptions page</li>
          <li>Review channel content to ensure appropriateness</li>
          <li>Unsubscribe from channels you deem unsuitable</li>
          <li>Talk to your child about why certain content isn't appropriate</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Disabling Features for Safety
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Disable Comments
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Prevent your child from reading or posting comments:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Settings → Privacy → Toggle "Disable Comments" ON</li>
          <li>Comments won't display on any video they watch</li>
          <li>They cannot post comments on any content</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Disable Autoplay
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Prevent endless viewing sessions:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Settings → Playback → Toggle "Autoplay" OFF</li>
          <li>Videos won't automatically play next content</li>
          <li>Child must manually select each video, giving you more control</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Screen Time Management
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Set Viewing Time Limits
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Use device-level parental controls to limit IndeeClips usage:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>iOS:</strong> Screen Time settings → App Limits → set daily time limit for IndeeClips</li>
          <li><strong>Android:</strong> Digital Wellbeing → App Timers → configure IndeeClips usage hours</li>
          <li><strong>Windows:</strong> Family Safety features to schedule allowed hours</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Reminder Breaks
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          IndeeClips can remind users to take breaks:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Settings → Notifications → Enable "Take a Break" reminders</li>
          <li>Set interval (every 30 minutes, every hour, etc.)</li>
          <li>App pauses playback and suggests closing IndeeClips</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Teaching Safe Viewing Habits
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Have Open Conversations
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Ask what they're watching and why they enjoy it</li>
          <li>Watch videos together sometimes to understand their interests</li>
          <li>Explain why certain content isn't appropriate for their age</li>
          <li>Encourage them to tell you if they see something that makes them uncomfortable</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Teach Critical Thinking
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Not everything on the internet is true—teach them to question claims</li>
          <li>Explain the difference between educational content and entertainment</li>
          <li>Discuss why some creators use clickbait and how to recognize it</li>
          <li>Help them understand advertising and sponsorships</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Reporting Inappropriate Content
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          If your child encounters harmful content despite safety measures:
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Click three-dot menu on the video → "Report"</li>
          <li>Select violation type (sexual content, violence, hate speech, etc.)</li>
          <li>Provide details about why it's inappropriate</li>
          <li>Block the channel to prevent future exposure</li>
          <li>For serious violations (child safety issues), contact contact@indeeclips.com immediately</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Red Flags to Watch For
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Monitor for these warning signs of problematic viewing habits:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Hiding screen when you enter the room</li>
          <li>Watching videos late at night or in secret</li>
          <li>Sudden behavior changes after watching certain content</li>
          <li>Repeating inappropriate language or concepts from videos</li>
          <li>Spending excessive time watching (more than 2-3 hours daily)</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Age-Appropriate Content Recommendations
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Ages 5-8
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Educational content (alphabet, numbers, science experiments)</li>
          <li>Animated stories and fairy tales</li>
          <li>DIY crafts and simple cooking</li>
          <li>Animal documentaries</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Ages 9-12
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>STEM tutorials and coding basics</li>
          <li>Age-appropriate gaming content</li>
          <li>Sports highlights and tutorials</li>
          <li>Hobby-based channels (art, music, robotics)</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Ages 13-17
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Educational content for school subjects</li>
          <li>Skill-building tutorials (video editing, photography)</li>
          <li>Career exploration and college prep</li>
          <li>Teen-appropriate entertainment and vlogs</li>
        </Box>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Balance Safety with Independence
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
            Effective parental controls combine technology tools with open communication. Use the safety features IndeeClips provides, but also talk to your kids about what they watch and why. As they grow, gradually give them more freedom while maintaining trust and awareness. The goal is to raise digitally literate, responsible viewers—not just restrict access.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Questions about child safety or parental controls? Contact{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
            .
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ParentalControls;
